import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import api from "@/lib/api";
import { fmtDate, fmtTime, Loading, ProStatusBadge, EmptyState } from "@/components/common";

export default function ProDaFare({ onClose }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/pro/interventions").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const groups = useMemo(() => {
    const pending = (items || []).filter((i) => ["nuova", "presa_in_carico", "programmata"].includes(i.status));
    const byComune = {};
    pending.forEach((i) => {
      const comune = i.cemetery?.comune || "Senza comune";
      (byComune[comune] = byComune[comune] || { comune, items: [] }).items.push(i);
    });
    const out = Object.values(byComune).sort((a, b) => a.comune.localeCompare(b.comune));
    out.forEach((g) =>
      g.items.sort((a, b) => {
        const da = a.scheduled_date || "9999";
        const db = b.scheduled_date || "9999";
        if (da !== db) return da < db ? -1 : 1;
        const ga = a.grave || {};
        const gb = b.grave || {};
        return (ga.map_y ?? 0) - (gb.map_y ?? 0) || (ga.map_x ?? 0) - (gb.map_x ?? 0);
      })
    );
    return out;
  }, [items]);

  const total = groups.reduce((s, g) => s + g.items.length, 0);

  return (
    <div className="fixed inset-0 z-[80] bg-[#f8fafc] overflow-y-auto" data-testid="pro-todo-full" role="dialog" aria-modal="true">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-16">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="eyebrow mb-1">Interventi</p>
            <h1 className="font-serif text-3xl text-navy">Da effettuare: {total}</h1>
          </div>
          <button
            data-testid="todo-close"
            onClick={onClose}
            className="p-3 rounded-full border border-[#e2e8f0] bg-white text-navy hover:bg-[#f0f7ff] transition-colors duration-200"
            aria-label="Chiudi"
          >
            <X size={22} />
          </button>
        </div>

        {items === null ? (
          <Loading />
        ) : groups.length === 0 ? (
          <EmptyState title="Nessun intervento da effettuare" />
        ) : (
          <div className="space-y-6">
            {groups.map((g) => (
              <div key={g.comune} className="card-soft p-5" data-testid={`todo-comune-${g.comune}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <p className="eyebrow">{g.comune}</p>
                  <p className="text-xs font-bold text-[#4a7c59]" data-testid={`todo-count-${g.comune}`}>{g.items.length} interventi</p>
                </div>
                <div className="space-y-2">
                  {g.items.map((i, idx) => (
                    <Link key={i.id} to={`/area-pro/interventi/${i.id}`} data-testid={`todo-item-${i.id}`}
                      className="flex items-center gap-3 py-2 border-b border-[#f1f5f9] last:border-0">
                      <span className="w-7 h-7 rounded-full bg-[#4a7c59] text-white text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                      {(i.grave?.photo || i.grave?.admin_photo) && (
                        <img src={i.grave.admin_photo || i.grave.photo} alt="Foto luogo" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-navy text-sm truncate">{i.customer_name} — {i.service}</p>
                        <p className="text-xs text-muted-soft truncate">
                          {i.grave ? `${i.grave.deceased_name} ${i.grave.deceased_surname} · ` : ""}{i.cemetery?.name}
                          {i.scheduled_date ? ` · ${fmtDate(i.scheduled_date)} ${fmtTime(i.scheduled_date)}` : " · In attesa di programmazione"}
                        </p>
                      </div>
                      <ProStatusBadge status={i.status} />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
